import { createContext, Dispatch } from 'react';

import { getSideNavPreference } from 'app/api/cookie';

import { getDeviceFromWidth } from './helpers';

export type Device = 'desktop' | 'mobile' | 'tablet';
export type UiTheme = 'dark' | 'light' | 'system';

export interface GeneralState {
  beamerMessageCount: number;
  currentDevice: Device;
  currentOpenFilterButton: null | string;
  isServiceWorkerUpdated: boolean;
  isSideNavOpen: boolean;
  uiTheme: UiTheme;
}

interface ResetAction {
  type: 'reset';
}
interface SetBeamerMessageCountAction {
  payload: {
    beamerMessageCount: number;
  };
  type: 'setBeamerMessageCount';
}
interface SetDeviceAction {
  payload: {
    currentDevice: Device;
  };
  type: 'setDevice';
}
interface SetOpenFilterButtonAction {
  payload: {
    currentOpenFilterButton: null | string;
  };
  type: 'setOpenFilterButton';
}
interface ToggleSideNavAction {
  type: 'toggleSideNav';
}
interface UpdateServiceWorkerAction {
  type: 'updateServiceWorker';
}
interface UpdateUiTheme {
  payload: {
    uiTheme: UiTheme;
  };
  type: 'updateUiTheme';
}

export type GeneralContextAction =
  | ResetAction
  | SetBeamerMessageCountAction
  | SetDeviceAction
  | SetOpenFilterButtonAction
  | ToggleSideNavAction
  | UpdateServiceWorkerAction
  | UpdateUiTheme;

export type GeneralDispatch = Dispatch<GeneralContextAction>;

function getInitialDevice(): Device {
  if (window) {
    return getDeviceFromWidth(window.innerWidth);
  }

  return 'desktop';
}

function getInitialSideNavOpenState(): boolean {
  const device = getInitialDevice();

  if (device !== 'desktop') {
    return false;
  }

  const sideNavPreference = getSideNavPreference();

  return sideNavPreference !== 'closed';
}

export const initialState: GeneralState = {
  beamerMessageCount: 0,
  currentDevice: getInitialDevice(),
  currentOpenFilterButton: null,
  isServiceWorkerUpdated: false,
  isSideNavOpen: getInitialSideNavOpenState(),
  uiTheme: 'system',
};

export const GeneralStateContext = createContext(initialState);
export const GeneralDispatchContext = createContext<GeneralDispatch>(undefined as unknown as GeneralDispatch);

GeneralStateContext.displayName = 'GeneralState';
GeneralDispatchContext.displayName = 'GeneralDispatch';
